import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, Plus, Pencil, Archive, EyeSlash, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = ['LICENSE','TERMS','PRIVACY','RISK','AGREEMENT','REGULATORY','OTHER'];

const empty = { title: '', doc_type: 'TERMS', description: '', language: 'en', version: '1.0', file_url: '', published: true };

const AdminLegalDocuments = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/legal-documents`);
      setDocs(res.data || []);
    } catch (e) { toast.error('Failed to load documents'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const openNew = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (d) => { setForm({ ...empty, ...d }); setEditingId(d.id); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/legal-documents/${editingId}`, form);
        toast.success('Document updated');
      } else {
        await axios.post(`${API}/admin/legal-documents`, form);
        toast.success('Document created');
      }
      setOpen(false); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const togglePublish = async (d) => {
    try {
      await axios.put(`${API}/admin/legal-documents/${d.id}`, { published: !d.published });
      toast.success(d.published ? 'Unpublished' : 'Published');
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const archive = async (d) => {
    if (!window.confirm(`Archive "${d.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/legal-documents/${d.id}`);
      toast.success('Archived');
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="p-8" data-testid="admin-legal-documents-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-brand-primary">Legal Documents</h1>
          <p className="text-slate-600 mt-1">Manage official documents shown to clients</p>
        </div>
        <Button onClick={openNew} data-testid="admin-legal-new-btn"><Plus size={16} className="mr-1" /> New Document</Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full text-sm" data-testid="admin-legal-table">
            <thead className="bg-slate-50 text-left uppercase text-xs tracking-wider text-slate-600">
              <tr>
                <th className="p-4">Title</th>
                <th className="p-4">Type</th>
                <th className="p-4">Lang</th>
                <th className="p-4">Version</th>
                <th className="p-4">Status</th>
                <th className="p-4">Updated</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="7" className="p-8 text-center"><div className="loader mx-auto" /></td></tr>
                : docs.length === 0 ? <tr><td colSpan="7" className="p-8 text-center text-slate-500">No documents yet</td></tr>
                : docs.map(d => (
                  <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-4 font-semibold">{d.title}<div className="text-xs text-slate-500 truncate max-w-xs">{d.description}</div></td>
                    <td className="p-4"><span className="px-2 py-1 rounded bg-slate-100 text-xs font-semibold">{d.doc_type}</span></td>
                    <td className="p-4 uppercase text-xs">{d.language}</td>
                    <td className="p-4 text-xs">v{d.version}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${d.archived ? 'bg-slate-200 text-slate-600' : d.published ? 'bg-trading-bullish-bg text-trading-bullish' : 'bg-yellow-100 text-yellow-800'}`}>
                        {d.archived ? 'ARCHIVED' : d.published ? 'PUBLISHED' : 'DRAFT'}
                      </span>
                    </td>
                    <td className="p-4 text-xs text-slate-500">{new Date(d.updated_at || d.created_at).toLocaleDateString()}</td>
                    <td className="p-4 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => openEdit(d)} data-testid={`admin-legal-edit-${d.id}`}><Pencil size={14} /></Button>
                        {!d.archived && (
                          <Button size="sm" variant="outline" onClick={() => togglePublish(d)} data-testid={`admin-legal-toggle-${d.id}`}>
                            {d.published ? <EyeSlash size={14} /> : <Eye size={14} />}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => archive(d)} data-testid={`admin-legal-archive-${d.id}`}><Archive size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingId ? 'Edit' : 'New'} Legal Document</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><Label>Title</Label><Input required value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="legal-form-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.doc_type} onValueChange={v=>setForm({...form,doc_type:v})}>
                  <SelectTrigger data-testid="legal-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Language</Label>
                <Select value={form.language} onValueChange={v=>setForm({...form,language:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="en">English</SelectItem><SelectItem value="it">Italian</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Version</Label><Input value={form.version} onChange={e=>setForm({...form,version:e.target.value})} /></div>
              <div className="flex items-end pb-1"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.published} onChange={e=>setForm({...form,published:e.target.checked})} /> Published</label></div>
            </div>
            <div>
              <Label>Document URL</Label>
              <Input required placeholder="https://example.com/document.pdf" value={form.file_url} onChange={e=>setForm({...form,file_url:e.target.value})} data-testid="legal-form-url" />
              <p className="text-xs text-slate-500 mt-1">Paste a URL to the PDF. File-upload endpoint coming soon.</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={()=>setOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1" data-testid="legal-form-submit">{editingId ? 'Save changes' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLegalDocuments;
